import { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function Campaigns() {
  const { selectedBusiness } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('bulk');
  const [form, setForm] = useState({ campaign_name: '', template_name: '', language: 'en_US', scheduled_at: '', pdf_url: '' });
  const [isSchedule, setIsSchedule] = useState(false);
  const [result, setResult] = useState(null);
  const [cronUrl, setCronUrl] = useState('');
  const [copied, setCopied] = useState('');
  const [showForm, setShowForm] = useState(true);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [extraVar1, setExtraVar1] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [mediaAudience, setMediaAudience] = useState('all');
  const [openerTemplate, setOpenerTemplate] = useState('');
  const [openerSending, setOpenerSending] = useState(false);
  const [openerResult, setOpenerResult] = useState(null);
  const [audienceMode, setAudienceMode] = useState('all');
  const [contactsDone, setContactsDone] = useState(false);
  const audienceRef = useRef(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const mediaFileRef = useRef();
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaCaption, setMediaCaption] = useState('');
  const [mediaSending, setMediaSending] = useState(false);
  const [mediaResult, setMediaResult] = useState(null);
  const [windowOpenContacts, setWindowOpenContacts] = useState([]);
  const [selectedWindowContacts, setSelectedWindowContacts] = useState([]);
  const [loadingWindowContacts, setLoadingWindowContacts] = useState(false);
  const [windowMediaFiles, setWindowMediaFiles] = useState([]);
  const [windowContactsPage, setWindowContactsPage] = useState(1);
  const WINDOW_CONTACTS_PER_PAGE = 20;
  const multiMediaRef = useRef();
  const windowMultiMediaRef = useRef();

  useEffect(() => { fetchTemplates(); fetchTags(); fetchContacts(); }, [selectedBusiness]);
  useEffect(() => {
    const handleClick = (e) => { if (audienceRef.current && !audienceRef.current.contains(e.target)) setAudienceOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchTemplates = async () => {
    if (!selectedBusiness?.id) return;
    try { const t = await api.get(`/templates?business_id=${selectedBusiness.id}`); setTemplates(Array.isArray(t.data) ? t.data : []); } catch (e) {}
  };
  const fetchTags = async () => {
    if (!selectedBusiness?.id) return;
    try { const r = await api.get(`/tags?business_id=${selectedBusiness.id}`); setTags(Array.isArray(r.data) ? r.data : []); } catch (e) {}
  };
  const uploadMultiMedia = async (files) => {
    const urls = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('business_id', selectedBusiness.id);
      try {
        const { data } = await api.post('/messages/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
        urls.push({ url: data.media_url, type: file.type === 'application/pdf' ? 'document' : (file.type.startsWith('video') ? 'video' : 'image'), name: file.name });
      } catch(e) {
        console.log('Upload error:', e.message);
      }
    }
    return urls;
  };

  const sendOpenerTemplate = async () => {
    if (!openerTemplate) return alert('Select a template!');
    if (!mediaFiles.length) return alert('Upload an image/video/pdf below first!');
    setOpenerSending(true);
    setOpenerResult(null);
    try {
      const uploaded = await uploadMultiMedia([mediaFiles[0]]);
      if (!uploaded.length) { setOpenerResult({ error: 'Media upload failed' }); setOpenerSending(false); return; }
      const basePayload = {
        business_id: selectedBusiness.id,
        template_name: openerTemplate,
        header_media_link: uploaded[0].url,
        header_media_type: uploaded[0].type,
        header_media_name: uploaded[0].name
      };
      if (mediaAudience !== 'all') {
        basePayload.tag_id = mediaAudience;
        const r = await api.post('/campaigns/send-opener-template', basePayload, { timeout: 300000 });
        setOpenerResult(r.data);
        setOpenerSending(false);
        if (r.data?.report_id) {
          setTimeout(async () => {
            try {
              const statusR = await api.get(`/campaigns/status/${r.data.report_id}`);
              setOpenerResult(prev => ({ ...prev, ...statusR.data, checked: true }));
            } catch (e) { console.log('Status check error:', e.message); }
          }, 12000);
        }
        return;
      }
      const allContactIds = contacts.map(c => c.id);
      const chunkSize = 30;
      const chunks = [];
      for (let i = 0; i < allContactIds.length; i += chunkSize) {
        chunks.push(allContactIds.slice(i, i + chunkSize));
      }
      let successCount = 0, failCount = 0;
      for (let ci = 0; ci < chunks.length; ci++) {
        try {
          await api.post('/campaigns/send-opener-template', { ...basePayload, contact_ids: chunks[ci] }, { timeout: 60000 });
          successCount++;
        } catch (chunkErr) {
          failCount++;
          console.log('Chunk send error:', chunkErr.message);
        }
        setOpenerResult({ message: `Progress: ${ci + 1}/${chunks.length} batches...` });
        if (ci < chunks.length - 1) await new Promise(r => setTimeout(r, 10000));
      }
      setOpenerResult({ message: `${successCount}/${chunks.length} batches sent successfully${failCount ? `, ${failCount} failed` : ''}` });
      setOpenerSending(false);
      return;
    } catch (e) { setOpenerResult({ error: e.response?.data?.error || e.message }); }
    setOpenerSending(false);
  };

  const sendMediaBulk = async () => {
    if (!mediaFiles.length) return alert('Select files!');
    const validFiles = mediaFiles.filter(f => {
      const isV = f.type.startsWith('video');
      const isP = f.type === 'application/pdf';
      const maxB = isP ? 100*1024*1024 : isV ? 16*1024*1024 : 5*1024*1024;
      return f.size <= maxB;
    });
    if (!validFiles.length) return alert('All selected files exceed the size limit. Remove or compress them first.');
    setMediaSending(true);
    try {
      const uploaded = await uploadMultiMedia(validFiles);
      if (!uploaded.length) { alert('Upload failed!'); setMediaSending(false); return; }
      if (mediaAudience !== 'all') {
        const payload = { business_id: selectedBusiness.id, media_files: uploaded, caption: mediaCaption, tag_id: mediaAudience };
        const r = await api.post('/campaigns/send-media-bulk', payload, { timeout: 300000 });
        setMediaResult(r.data);
        setMediaSending(false);
        return;
      }
      const allContactIds = contacts.map(c => c.id);
      const chunkSize = 30;
      const chunks = [];
      for (let i = 0; i < allContactIds.length; i += chunkSize) {
        chunks.push(allContactIds.slice(i, i + chunkSize));
      }
      let successCount = 0, failCount = 0;
      for (let ci = 0; ci < chunks.length; ci++) {
        try {
          await api.post('/campaigns/send-media-bulk', {
            business_id: selectedBusiness.id,
            media_files: uploaded,
            caption: mediaCaption,
            contact_ids: chunks[ci]
          }, { timeout: 60000 });
          successCount++;
        } catch (chunkErr) { failCount++; console.log('Chunk send error:', chunkErr.message); }
        setMediaResult({ message: `Progress: ${ci + 1}/${chunks.length} batches...` });
        if (ci < chunks.length - 1) await new Promise(r => setTimeout(r, 10000));
      }
      setMediaResult({ message: `${successCount}/${chunks.length} batches sent successfully${failCount ? `, ${failCount} failed` : ''}` });
    } catch(e) { alert('Something went wrong. Please try again.'); }
    setMediaSending(false);
  };

  const fetchWindowOpenContacts = async () => {
    if (!selectedBusiness?.id) return;
    setLoadingWindowContacts(true);
    try {
      const r = await api.get(`/campaigns/window-open-contacts?business_id=${selectedBusiness.id}`);
      setWindowOpenContacts(Array.isArray(r.data?.contacts) ? r.data.contacts : []);
      setWindowContactsPage(1);
    } catch (e) { console.log('Window open fetch error:', e.message); }
    setLoadingWindowContacts(false);
  };
  const toggleWindowContact = (id) => {
    setSelectedWindowContacts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const sendFreeMediaToSelected = async () => {
    if (!selectedWindowContacts.length) return alert('Select at least one contact!');
    if (!windowMediaFiles.length) return alert('Select files!');
    const validFiles = windowMediaFiles.filter(f => {
      const isV = f.type.startsWith('video');
      const isP = f.type === 'application/pdf';
      const maxB = isP ? 100*1024*1024 : isV ? 16*1024*1024 : 5*1024*1024;
      return f.size <= maxB;
    });
    if (!validFiles.length) return alert('All selected files exceed the size limit. Remove or compress them first.');
    setMediaSending(true);
    try {
      const uploaded = await uploadMultiMedia(validFiles);
      if (!uploaded.length) { alert('Upload failed!'); setMediaSending(false); return; }
      const chunkSize = 30;
      const chunks = [];
      for (let i = 0; i < selectedWindowContacts.length; i += chunkSize) {
        chunks.push(selectedWindowContacts.slice(i, i + chunkSize));
      }
      let successCount = 0, failCount = 0;
      for (let ci = 0; ci < chunks.length; ci++) {
        try {
          await api.post('/campaigns/send-media-bulk', {
            business_id: selectedBusiness.id,
            media_files: uploaded,
            caption: mediaCaption,
            contact_ids: chunks[ci]
          }, { timeout: 60000 });
          successCount++;
        } catch (chunkErr) { failCount++; console.log('Chunk send error:', chunkErr.message); }
        setMediaResult({ message: `Progress: ${ci + 1}/${chunks.length} batches...` });
        if (ci < chunks.length - 1) await new Promise(r => setTimeout(r, 10000));
      }
      setMediaResult({ message: `${successCount}/${chunks.length} batches sent successfully${failCount ? `, ${failCount} failed` : ''}` });
    } catch(e) { alert('Something went wrong. Please try again.'); }
    setMediaSending(false);
  };
  const fetchContacts = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const r = await api.get(`/contacts?business_id=${selectedBusiness.id}&limit=500`);
      setContacts(Array.isArray(r.data) ? r.data : (r.data?.contacts || []));
    } catch (e) {}
  };
  const [activeOnly, setActiveOnly] = useState(false);
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceCountLoading, setAudienceCountLoading] = useState(false);
  const fetchAudienceCount = async () => {
    if (!selectedBusiness?.id) return;
    if (audienceMode === 'tag' && !selectedTagId) { setAudienceCount(null); return; }
    setAudienceCountLoading(true);
    try {
      let url = `/contacts?business_id=${selectedBusiness.id}&limit=1`;
      if (audienceMode === 'tag' && selectedTagId) url += `&tag_id=${selectedTagId}`;
      if (activeOnly) url += `&active_only=true`;
      const r = await api.get(url);
      setAudienceCount(typeof r.data?.total === 'number' ? r.data.total : null);
    } catch (e) { setAudienceCount(null); }
    setAudienceCountLoading(false);
  };
  useEffect(() => { fetchAudienceCount(); }, [audienceMode, selectedTagId, activeOnly, selectedBusiness?.id]);

  const copyText = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 2000); };

  const getAudienceDropLabel = () => {
    if (audienceMode === 'tag' && selectedTagId) { const t = tags.find(t => String(t.id) === String(selectedTagId)); return t ? `Tag: ${t.name}` : 'Tags'; }
    if (audienceMode === 'contacts' && contactsDone) return `${selectedContactIds.length} Contacts Selected`;
    return '-- All Contacts --';
  };

  const selectedTag = tags.find(t => String(t.id) === String(selectedTagId));
  const selectedContacts = contacts.filter(c => selectedContactIds.includes(c.id));

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setMediaUrl(URL.createObjectURL(file));
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('business_id', selectedBusiness.id);
      const { data } = await api.post('/messages/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // mediaUrl already set as objectURL above - don't override
      // Store media_id for sending
      setForm(f => ({ ...f, pdf_url: data.media_url || '' }));
    } catch (e) {
      alert('Upload failed. Please try again.');
      setMediaFile(null);
    }
    setUploadingMedia(false);
  };

  const sendBulk = async () => {
    if (!form.template_name) return alert('Template required!');
    setSending(true); setResult(null);
    try {
      const payload = {
        business_id: selectedBusiness.id,
        campaign_name: form.campaign_name || form.template_name,
        template_name: form.template_name,
        pdf_url: form.pdf_url || '',
        language: form.language,
      };
      if (isSchedule && form.scheduled_at) payload.scheduled_at = form.scheduled_at;
      if (extraVar1) payload.variables = JSON.stringify([extraVar1]);
      if (audienceMode === 'tag' && selectedTagId) payload.tag_id = selectedTagId;
      else if (audienceMode === 'contacts' && selectedContactIds.length) payload.contact_ids = selectedContactIds;
      else payload.contact_ids = [];
      const r = await api.post('/campaigns/send-bulk', payload);
      setResult(r.data);
    } catch (e) { setResult({ error: e.response?.data?.error || 'Failed' }); }
    setSending(false);
  };

  const createApiCampaign = async () => {
    if (!form.template_name) return alert('Template required!');
    setSending(true);
    try {
      const r = await api.post('/campaigns/create-api', { business_id: selectedBusiness.id, campaign_name: form.campaign_name || form.template_name, template_name: form.template_name, language: form.language });
      setCronUrl(`https://vbot.co.in/api/sendBulkCampaign/${r.data.token}`);
      setResult({ success: true, msg: 'API Campaign created!' });
    } catch (e) { setResult({ error: e.response?.data?.error || 'Failed' }); }
    setSending(false);
  };

  const scheduleCampaign = async () => {
    if (!form.template_name || !form.scheduled_at) return alert('Template and date/time required!');
    setSending(true); setResult(null);
    try {
      const schedPayload = { business_id: selectedBusiness.id, template_name: form.template_name, language: form.language, scheduled_at: form.scheduled_at };
      if (audienceMode === 'tag' && selectedTagId) schedPayload.tag_id = selectedTagId;
      else if (audienceMode === 'contacts' && selectedContactIds.length) schedPayload.contact_ids = selectedContactIds;
      await api.post('/campaigns/schedule', schedPayload);
      setResult({ success: true, msg: 'Campaign scheduled!' });
    } catch (e) { setResult({ error: e.response?.data?.error || 'Failed' }); }
    setSending(false);
  };

  const [showConfirm, setShowConfirm] = useState(false);
  const handleSave = () => { setShowConfirm(true); };
  const handleConfirmed = () => { setShowConfirm(false); if (tab === 'bulk') sendBulk(); else if (tab === 'api') createApiCampaign(); else scheduleCampaign(); };

  const handleCancel = () => {
    setShowForm(false);
    setForm({ campaign_name: '', template_name: '', language: 'en_US', scheduled_at: '', pdf_url: '' });
    setSelectedTagId(''); setSelectedContactIds([]); setAudienceMode('all'); setContactsDone(false);
    setResult(null); setCronUrl(''); setMediaFile(null); setMediaUrl('');
  };

  const selectedTemplate = templates.find(t => t.name === form.template_name);
  const previewHeader = selectedTemplate?.components?.find(c => c.type === 'HEADER');
  const previewBody = selectedTemplate?.components?.find(c => c.type === 'BODY')?.text || 'Boost Your Marketing!\n\nLeverage WhatsApp automation to engage your audience effectively.';
  const previewFooter = selectedTemplate?.components?.find(c => c.type === 'FOOTER')?.text || '';
  const previewButtons = selectedTemplate?.components?.find(c => c.type === 'BUTTONS')?.buttons || [];
  const headerFormat = previewHeader?.format;
  const needsMedia = headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT';
  const inp = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', color: '#111', background: '#fff' };
  const namesPreview = selectedContacts.slice(0, 3).map(c => c.name || c.phone).join(', ') + (selectedContacts.length > 3 ? ` +${selectedContacts.length - 3} more` : '');

  return (
    <div style={{ padding: '16px 20px', fontFamily: 'DM Sans, sans-serif', background: '#f4f6f9', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111', margin: 0 }}>Campaigns</h2>
        {!showForm && <button onClick={() => setShowForm(true)} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>+ Create Campaign</button>}
      </div>
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden', minHeight: '80vh' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>🔄</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#15803d' }}>Campaign</span>
            </div>
            {cronUrl
              ? <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 14, color: '#374151', fontWeight: 600 }}>Cron Job URL: <span style={{ color: '#2563eb' }}>{cronUrl}</span></span>
                  <button onClick={() => copyText(cronUrl, 'cron')} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>{copied === 'cron' ? 'Copied!' : 'Copy'}</button>
                </div>
              : null}
          </div>
          <div style={{ display: 'flex', gap: 28, padding: '16px 24px', borderBottom: '1px solid #f3f4f6' }}>
            {[{ id: 'bulk', label: 'Bulk Campaign' }, { id: 'api', label: 'API Campaign' }, { id: 'media', label: '📸 Media Campaign' }].map(t => (
              <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: tab === t.id ? '#15803d' : '#374151' }}>
                <input type="checkbox" checked={tab === t.id} onChange={() => { setTab(t.id); setResult(null); setCronUrl(''); }} style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: 'pointer' }} />
                {t.label}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', minHeight: 'calc(80vh - 120px)' }}>
            <div style={{ flex: 1, padding: '28px 32px', borderRight: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 24 }}>
                {tab === 'bulk' ? 'Bulk Campaign' : tab === 'api' ? 'API Campaign' : 'Schedule Campaign'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {tab === 'bulk' && (<>
                  <div>
                    <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Campaign Name</label>
                    <input value={form.campaign_name} onChange={e => setForm({ ...form, campaign_name: e.target.value })} placeholder="Enter Campaign Name" style={inp} />
                  </div>
                  <div>
                    <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Select Audience</label>
                    <select value={audienceMode} onChange={e => { setAudienceMode(e.target.value); setSelectedTagId(''); setSelectedContactIds([]); setContactsDone(false); }} style={inp}>
                      <option value="all">All Contacts</option>
                      <option value="tag">By Tag</option>
                    </select>
                  </div>
                  {audienceMode === 'tag' && (
                    <div>
                      <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Select Tag</label>
                      <select value={selectedTagId} onChange={e => setSelectedTagId(e.target.value)} style={inp}>
                        <option value="">-- Select Tag --</option>
                        {tags.map(tag => (
                          <option key={tag.id} value={String(tag.id)}>{tag.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, padding: '10px 12px', border: activeOnly ? '2px solid #6366f1' : '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
                    Only send to engaged contacts (read or replied within the last 90 days)
                  </label>
                  {(audienceMode === 'all' || (audienceMode === 'tag' && selectedTagId)) && (
                    <div style={{ padding: '12px 16px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22 }}>👥</span>
                      {audienceCountLoading ? (
                        <span style={{ fontSize: 13, color: '#6b7280' }}>Calculating...</span>
                      ) : (
                        <>
                          <span style={{ fontSize: 15, fontWeight: 700, color: '#15803d' }}>{audienceCount ?? 0} Contacts</span>
                          <span style={{ fontSize: 13, color: '#6b7280' }}>will receive this campaign</span>
                        </>
                      )}
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Select Template</label>
                    <select value={form.template_name} onChange={e => setForm({ ...form, template_name: e.target.value })} style={inp}>
                      <option value="">-- Select Template --</option>
                      {templates.map((t, i) => <option key={i} value={t.name}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ display: needsMedia ? 'block' : 'none' }}>
                      <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                        📎 Attach Media (Optional)
                        <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af', marginLeft: 8 }}>Required for this template</span>
                      </label>
                      <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, padding: '16px', textAlign: 'center', background: mediaUrl ? '#f0fdf4' : '#fafafa' }}>
                        {mediaUrl ? (
                          <div>
                            <div style={{ color: '#16a34a', fontWeight: 600, fontSize: 14, marginBottom: 8 }}>✅ {mediaFile?.name || 'File uploaded'}</div>
                            <button onClick={() => { setMediaFile(null); setMediaUrl(''); }} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#dc2626' }}>Remove</button>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>
                              {uploadingMedia ? '⏳ Uploading...' : `Click to upload ${headerFormat === 'IMAGE' ? 'image' : headerFormat === 'VIDEO' ? 'video' : 'document'}`}
                            </div>
                            <button onClick={() => mediaFileRef.current?.click()} disabled={uploadingMedia} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                              {uploadingMedia ? 'Uploading...' : '📎 Choose File'}
                            </button>
                          </div>
                        )}
                        <input ref={mediaFileRef} type="file" style={{ display: 'none' }}
                          accept='image/*,video/*,application/pdf,.pdf,.doc,.docx'
                          onChange={handleMediaUpload} />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>PDF/Media URL (Optional)</label>
                      <input value={form.pdf_url} onChange={e => setForm({ ...form, pdf_url: e.target.value })} placeholder="https://example.com/file.pdf" style={inp} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderTop: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: 18 }}>📅</span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: isSchedule ? '#15803d' : '#374151' }}>Is Schedule?</span>
                    <div onClick={() => setIsSchedule(!isSchedule)} style={{ width: 44, height: 24, borderRadius: 12, background: isSchedule ? '#16a34a' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: isSchedule ? 22 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                  {isSchedule && (
                    <div>
                      <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Schedule Date and Time</label>
                      <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} style={inp} />
                    </div>
                  )}
                  {result && (
                    <div style={{ padding: 12, borderRadius: 8, background: result.error ? '#fef2f2' : '#f0fdf4', border: `1px solid ${result.error ? '#fecaca' : '#bbf7d0'}` }}>
                      <p style={{ margin: 0, color: result.error ? '#dc2626' : '#16a34a', fontWeight: 600, fontSize: 13 }}>
                        {result.error ? '❌ ' + result.error : '✅ ' + (result.msg || `Campaign started! ${result.total || 0} contacts queued (processing in background)`)}
                      </p>
                    </div>
                  )}
                </>)}
                {tab === 'media' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Select Template */}
                    <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 12, padding: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: '#713f12', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        📋 Select Template
                      </label>
                      <select value={openerTemplate} onChange={e => setOpenerTemplate(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                        <option value="">-- Select Template --</option>
                        {templates.map((t, i) => <option key={i} value={t.name}>{t.name}</option>)}
                      </select>
                      {openerResult && (
                        <p style={{ marginTop: 8, fontSize: 13, color: openerResult.error ? '#dc2626' : (openerResult.message ? '#16a34a' : (openerResult.checked && openerResult.failed > 0 ? '#dc2626' : '#16a34a')) }}>
                          {openerResult.error
                            ? '❌ ' + openerResult.error
                            : openerResult.message
                              ? '✅ ' + openerResult.message
                              : !openerResult.checked
                                ? '📤 Sent: ' + openerResult.sent + ' | Failed: ' + openerResult.failed + ' (checking delivery status...)'
                                : '✅ Delivered: ' + (openerResult.delivered || 0) + ' | Read: ' + (openerResult.read || 0) + ' | Pending: ' + (openerResult.sent || 0) + ' | Failed: ' + (openerResult.failed || 0)}
                        </p>
                      )}
                    </div>
                    {/* Audience */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        👥 Select Audience
                      </label>
                      <select value={mediaAudience} onChange={e => {
                        const val = e.target.value;
                        setMediaAudience(val);
                        if (val === 'all') setSelectedContactIds(contacts.map(c => c.id));
                      }} style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer' }}>
                        <option value="all">All Contacts ({contacts.length})</option>
                        {tags.map(t => <option key={t.id} value={t.id}>🏷️ {t.name}</option>)}
                      </select>
                      {contacts.length > 0 && (
                        <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 20, padding: '4px 12px', fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                          ✅ {mediaAudience === 'all' ? contacts.length + ' contacts' : (tags.find(t=>String(t.id)===String(mediaAudience))?.name || 'Tag') + ' (' + (tags.find(t=>String(t.id)===String(mediaAudience))?.contact_count || 0) + ' contacts)'} selected
                        </div>
                      )}
                    </div>

                    {/* File Upload */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8, display: 'block' }}>📎 Media Files</label>
                      <div onClick={() => multiMediaRef.current?.click()}
                        style={{ border: '2px dashed #a3e635', borderRadius: 10, padding: '20px', textAlign: 'center', cursor: 'pointer', background: '#f7fee7', transition: 'all 0.2s' }}>
                        <div style={{ fontSize: 28 }}>📸</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#4d7c0f', marginTop: 4 }}>Click to select images/videos/PDF</div>
                        <div style={{ fontSize: 11, color: '#84cc16', marginTop: 2 }}>*Image max 5MB · Video max 16MB · PDF max 100MB · Multiple files supported</div>
                        <div style={{ fontSize: 10, color: '#a3a3a3', marginTop: 2 }}>*Mandatory limit — oversized files won't be sent</div>
                      </div>
                      <input type="file" multiple accept="image/*,video/*,application/pdf" ref={multiMediaRef}
                        onChange={e => setMediaFiles(prev => [...prev, ...Array.from(e.target.files)])}
                        style={{ display: 'none' }} />
                      {mediaFiles.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{mediaFiles.length} file{mediaFiles.length > 1 ? 's' : ''} selected</span>
                            <span onClick={() => setMediaFiles([])} style={{ fontSize: 12, color: '#ef4444', cursor: 'pointer' }}>Clear all</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            {mediaFiles.map((f, i) => {
                              const isVideo = f.type.startsWith('video');
                              const isPdf = f.type === 'application/pdf';
                              const maxBytes = isPdf ? 100*1024*1024 : isVideo ? 16*1024*1024 : 5*1024*1024;
                              const isOversized = f.size > maxBytes;
                              const previewUrl = isPdf ? null : URL.createObjectURL(f);
                              return (
                                <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', aspectRatio: '1', background: isOversized ? '#fef2f2' : '#f1f5f9', border: isOversized ? '2px solid #ef4444' : '1px solid #e2e8f0' }}>
                                  {isOversized && (
                                    <div style={{ position: 'absolute', top: 4, left: 4, background: '#ef4444', borderRadius: 4, padding: '2px 6px', zIndex: 2 }}>
                                      <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>Too large</span>
                                    </div>
                                  )}
                                  {isPdf
                                    ? <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2' }}>
                                        <span style={{ fontSize: 28 }}>📄</span>
                                      </div>
                                    : isVideo
                                    ? <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1e293b' }}>
                                        <span style={{ fontSize: 28 }}>🎬</span>
                                      </div>
                                    : <img src={previewUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onLoad={() => URL.revokeObjectURL(previewUrl)} />
                                  }
                                  <div onClick={() => setMediaFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: 4, left: 4, width: 16, height: 16, background: '#3b82f6', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>✓</span>
                                  </div>
                                  <div onClick={() => setMediaFiles(prev => prev.filter((_, idx) => idx !== i))}
                                    style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, background: 'rgba(0,0,0,0.5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <span style={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>✕</span>
                                  </div>
                                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', padding: '4px 6px' }}>
                                    <div style={{ fontSize: 10, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>{f.name}</div>
                                    <div style={{ fontSize: 14, color: isOversized ? '#ff9b9b' : '#8fe6b0', fontWeight: 700 }}>{(f.size/1024/1024).toFixed(1)} MB</div>
                                  </div>
                                </div>
                              );
                            })}
                            <div onClick={() => multiMediaRef.current?.click()}
                              style={{ borderRadius: 8, aspectRatio: '1', background: '#f8fafc', border: '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 4 }}>
                              <span style={{ fontSize: 20, color: '#94a3b8' }}>+</span>
                              <span style={{ fontSize: 10, color: '#94a3b8' }}>Add more</span>
                            </div>
                          </div>
                          {(() => {
                            const oversized = mediaFiles.filter(f => {
                              const isV = f.type.startsWith('video');
                              const isP = f.type === 'application/pdf';
                              const maxB = isP ? 100*1024*1024 : isV ? 16*1024*1024 : 5*1024*1024;
                              return f.size > maxB;
                            });
                            if (!oversized.length) return null;
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fef2f2', border: '1px solid #ef4444', borderRadius: 8, padding: '12px 14px', marginTop: 12 }}>
                                <span style={{ fontSize: 20 }}>⚠️</span>
                                <div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>{oversized.length} file{oversized.length > 1 ? 's are' : ' is'} too large to send</div>
                                  <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2, lineHeight: 1.5 }}>
                                    {oversized.map((f, idx) => {
                                      const isV = f.type.startsWith('video');
                                      const isP = f.type === 'application/pdf';
                                      const maxMB = isP ? 100 : isV ? 16 : 5;
                                      const sizeMB = (f.size/1024/1024).toFixed(1);
                                      const overMB = (f.size/1024/1024 - maxMB).toFixed(1);
                                      return <div key={idx}>{f.name} is {sizeMB} MB — {overMB} MB over the {maxMB} MB limit.</div>;
                                    })}
                                    <div style={{ marginTop: 4 }}>These will be skipped automatically when you send.</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Caption */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8, display: 'block' }}>💬 Caption <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                      <input value={mediaCaption} onChange={e => setMediaCaption(e.target.value)}
                        placeholder="Add a message with your media..."
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }} />
                    </div>

                    {/* Result */}
                    {mediaResult && (
                      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>✅</span>
                        <div>
                          <div style={{ fontWeight: 700, color: '#15803d', fontSize: 13 }}>Campaign Sent Successfully!</div>
                          <div style={{ fontSize: 12, color: '#16a34a' }}>Sent: {mediaResult.sent} · Failed: {mediaResult.failed}</div>
                        </div>
                      </div>
                    )}

                    {/* Send Button */}
                    <button onClick={sendOpenerTemplate} disabled={openerSending}
                      style={{ background: openerSending ? '#86efac' : 'linear-gradient(135deg, #16a34a, #15803d)', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 20px', fontSize: 14, fontWeight: 700, cursor: openerSending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: openerSending ? 'none' : '0 4px 12px rgba(22,163,74,0.3)', transition: 'all 0.2s' }}>
                      {openerSending ? (
                        <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Uploading & Sending...</>
                      ) : (
                        <><span>📤</span> Send Template to {mediaAudience === 'all' ? contacts.length + ' Contacts' : (tags.find(t=>String(t.id)===String(mediaAudience))?.contact_count || 0) + ' Contacts (' + (tags.find(t=>String(t.id)===String(mediaAudience))?.name || '') + ')'}</>
                      )}
                    </button>
                    <div style={{ border: '1px solid #dbeafe', borderRadius: 10, padding: 14, marginTop: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#1d4ed8' }}>🟢 Window Open Contacts</span>
                        <button onClick={fetchWindowOpenContacts} disabled={loadingWindowContacts}
                          style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {loadingWindowContacts ? 'Loading...' : 'Fetch Contacts'}
                        </button>
                      </div>
                      {windowOpenContacts.length > 0 && (
                        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
                          <label style={{ display: 'block', padding: '4px 0', fontWeight: 700, fontSize: 13, borderBottom: '1px solid #eee', marginBottom: 4 }}>
                            <input type="checkbox"
                              checked={selectedWindowContacts.length === windowOpenContacts.length}
                              onChange={(e) => setSelectedWindowContacts(e.target.checked ? windowOpenContacts.map(c => c.id) : [])}
                              style={{ marginRight: 8 }} />
                            Select All ({windowOpenContacts.length})
                          </label>
                          <div style={{ minHeight: 260 }}>
                            {windowOpenContacts
                              .slice((windowContactsPage - 1) * WINDOW_CONTACTS_PER_PAGE, windowContactsPage * WINDOW_CONTACTS_PER_PAGE)
                              .map(contact => (
                                <label key={contact.id} style={{ display: 'block', padding: '4px 0', fontSize: 13 }}>
                                  <input type="checkbox"
                                    checked={selectedWindowContacts.includes(contact.id)}
                                    onChange={() => toggleWindowContact(contact.id)}
                                    style={{ marginRight: 8 }} />
                                  {contact.name || 'No Name'} — {contact.phone}
                                </label>
                              ))}
                          </div>
                          {windowOpenContacts.length > WINDOW_CONTACTS_PER_PAGE && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                              <button onClick={() => setWindowContactsPage(p => Math.max(1, p - 1))} disabled={windowContactsPage === 1}
                                style={{ border: 'none', background: 'none', fontSize: 16, cursor: windowContactsPage === 1 ? 'not-allowed' : 'pointer', color: windowContactsPage === 1 ? '#cbd5e1' : '#1d4ed8', fontWeight: 700 }}>
                                {'<'}
                              </button>
                              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                                Page {windowContactsPage} of {Math.ceil(windowOpenContacts.length / WINDOW_CONTACTS_PER_PAGE)}
                              </span>
                              <button onClick={() => setWindowContactsPage(p => Math.min(Math.ceil(windowOpenContacts.length / WINDOW_CONTACTS_PER_PAGE), p + 1))}
                                disabled={windowContactsPage === Math.ceil(windowOpenContacts.length / WINDOW_CONTACTS_PER_PAGE)}
                                style={{ border: 'none', background: 'none', fontSize: 16, cursor: windowContactsPage === Math.ceil(windowOpenContacts.length / WINDOW_CONTACTS_PER_PAGE) ? 'not-allowed' : 'pointer', color: windowContactsPage === Math.ceil(windowOpenContacts.length / WINDOW_CONTACTS_PER_PAGE) ? '#cbd5e1' : '#1d4ed8', fontWeight: 700 }}>
                                {'>'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {windowOpenContacts.length === 0 && !loadingWindowContacts && (
                        <p style={{ fontSize: 12, color: '#9ca3af', margin: '4px 0' }}>Click "Fetch Contacts" to load the list.</p>
                      )}
                    </div>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginTop: 4 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 6, display: 'block' }}>📎 Media for Window Open Contacts</label>
                      <div onClick={() => windowMultiMediaRef.current?.click()}
                        style={{ border: '2px dashed #a3e635', borderRadius: 8, padding: '12px', textAlign: 'center', cursor: 'pointer', background: '#f7fee7' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#4d7c0f' }}>Click to select images/videos/PDF</div>
                        <div style={{ fontSize: 10, color: '#84cc16', marginTop: 2 }}>Image max 5MB · Video max 16MB · PDF max 100MB</div>
                      </div>
                      <input type="file" multiple accept="image/*,video/*,application/pdf" ref={windowMultiMediaRef}
                        onChange={e => setWindowMediaFiles(prev => [...prev, ...Array.from(e.target.files)])}
                        style={{ display: 'none' }} />
                      {windowMediaFiles.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{windowMediaFiles.length} file{windowMediaFiles.length > 1 ? 's' : ''} selected</span>
                            <span onClick={() => setWindowMediaFiles([])} style={{ fontSize: 11, color: '#ef4444', cursor: 'pointer' }}>Clear all</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                            {windowMediaFiles.map((f, i) => {
                              const isVideo = f.type.startsWith('video');
                              const isPdf = f.type === 'application/pdf';
                              const maxBytes = isPdf ? 100*1024*1024 : isVideo ? 16*1024*1024 : 5*1024*1024;
                              const isOversized = f.size > maxBytes;
                              const previewUrl = isPdf ? null : URL.createObjectURL(f);
                              return (
                                <div key={i} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', aspectRatio: '1', background: isOversized ? '#fef2f2' : '#f1f5f9', border: isOversized ? '2px solid #ef4444' : '1px solid #e2e8f0' }}>
                                  {isPdf
                                    ? <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 20 }}>📄</span></div>
                                    : isVideo
                                    ? <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1e293b' }}><span style={{ fontSize: 20 }}>🎬</span></div>
                                    : <img src={previewUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onLoad={() => URL.revokeObjectURL(previewUrl)} />
                                  }
                                  <div onClick={() => setWindowMediaFiles(prev => prev.filter((_, idx) => idx !== i))}
                                    style={{ position: 'absolute', top: 2, right: 2, width: 14, height: 14, background: 'rgba(0,0,0,0.5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, lineHeight: 1 }}>✕</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <button onClick={sendFreeMediaToSelected} disabled={mediaSending || !selectedWindowContacts.length}
                      style={{ background: mediaSending ? '#93c5fd' : (!selectedWindowContacts.length ? '#cbd5e1' : 'linear-gradient(135deg, #2563eb, #1d4ed8)'), color: '#fff', border: 'none', borderRadius: 10, padding: '13px 20px', fontSize: 14, fontWeight: 700, cursor: (mediaSending || !selectedWindowContacts.length) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: mediaSending ? 'none' : '0 4px 12px rgba(37,99,235,0.3)', transition: 'all 0.2s' }}>
                      {mediaSending ? (
                        <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Uploading & Sending...</>
                      ) : (
                        <><span>💬</span> Send Free Media ({selectedWindowContacts.length} Selected)</>
                      )}
                    </button>
                    <p style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', margin: 0 }}>
                      "Send Free Media" will be delivered only to contacts whose 24-hour window is open (i.e., they clicked "Yes I am interested").
                    </p>
                  </div>
                )}
                {tab === 'api' && (<>
                  <div>
                    <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Campaign Name</label>
                    <input value={form.campaign_name} onChange={e => setForm({ ...form, campaign_name: e.target.value })} placeholder="Enter Campaign Name" style={inp} />
                  </div>
                  <div>
                    <label style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Select Template</label>
                    <select value={form.template_name} onChange={e => setForm({ ...form, template_name: e.target.value })} style={inp}>
                      <option value="">-- Select Template --</option>
                      {templates.map((t, i) => <option key={i} value={t.name}>{t.name}</option>)}
                    </select>
                  </div>
                  {result && (
                    <div style={{ padding: 12, borderRadius: 8, background: result.error ? '#fef2f2' : '#f0fdf4', border: `1px solid ${result.error ? '#fecaca' : '#bbf7d0'}` }}>
                      <p style={{ margin: 0, color: result.error ? '#dc2626' : '#16a34a', fontWeight: 600, fontSize: 13 }}>{result.error ? '❌ ' + result.error : '✅ ' + result.msg}</p>
                    </div>
                  )}
                </>)}

              </div>
            </div>
            <div style={{ width: 380, padding: '48px 24px 32px', background: '#f9fafb', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: 320, borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <div style={{ background: '#075E54', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff' }}>
                    {selectedBusiness?.name?.[0]?.toUpperCase() || 'V'}
                  </div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{selectedBusiness?.name || 'VBOT'}</div>
                </div>
                <div style={{ background: '#e5ddd5', padding: 12, minHeight: 280 }}>
                  <div style={{ background: '#fff', borderRadius: '0 10px 10px 10px', maxWidth: '92%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                    {previewHeader && (
                      <div style={{ background: '#f0f0f0', padding: '10px 14px', borderBottom: '1px solid #e0e0e0' }}>
                        {previewHeader.format === 'TEXT' && <div style={{ fontWeight: 700, fontSize: 15, color: '#111' }}>{previewHeader.text}</div>}
                        {previewHeader.format === 'IMAGE' && (mediaUrl ? <img src={mediaUrl} alt='preview' style={{ width: '100%', borderRadius: 4, display: 'block' }} /> : <div style={{ textAlign: 'center', padding: '8px', color: '#666', fontSize: 13 }}>🖼️ Image Header</div>)}
                        {previewHeader.format === 'VIDEO' && <div style={{ textAlign: 'center', padding: '8px', color: '#666', fontSize: 13 }}>🎥 Video Header</div>}
                        {previewHeader.format === 'DOCUMENT' && (mediaUrl || form.pdf_url
    ? <a href={form.pdf_url || mediaUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0f4ff', borderRadius: 6, textDecoration: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: 28 }}>📄</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a' }}>{mediaFile?.name || 'Document'}</div>
          <div style={{ fontSize: 11, color: '#0093FF' }}>Click to open ↗</div>
        </div>
      </a>
    : <div style={{ textAlign: 'center', padding: '8px', color: '#666', fontSize: 13 }}>📄 Document Header</div>
  )}
                      </div>
                    )}
                    <div style={{ padding: '12px 14px', fontSize: 14, lineHeight: 1.7, color: '#111', whiteSpace: 'pre-wrap' }}>{previewBody}</div>
                    {previewFooter ? <div style={{ padding: '4px 14px 10px', fontSize: 12, color: '#8696a0', borderTop: '1px solid #f0f0f0' }}>{previewFooter}</div> : null}
                    {previewButtons.length > 0 && (
                      <div style={{ borderTop: '1px solid #e0e0e0' }}>
                        {previewButtons.map((btn, i) => (
                          <div key={i} style={{ padding: '10px 14px', textAlign: 'center', color: '#0093FF', fontWeight: 600, fontSize: 14, borderBottom: i < previewButtons.length - 1 ? '1px solid #e0e0e0' : 'none' }}>{btn.text}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {!form.template_name && <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 12, textAlign: 'center' }}>Select a template to preview</p>}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #f3f4f6' }}>
            <button onClick={handleCancel} style={{ padding: '10px 28px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>Cancel</button>
            <button onClick={handleSave} disabled={sending} style={{ padding: '10px 28px', borderRadius: 8, border: 'none', background: sending ? '#9ca3af' : '#16a34a', color: '#fff', fontWeight: 700, fontSize: 14, cursor: sending ? 'not-allowed' : 'pointer' }}>
              {sending ? 'Saving...' : tab === 'schedule' ? 'Schedule Campaign' : 'Save Campaign'}
            </button>
            {showConfirm && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>⚠️ Confirm Send</div>
                  <div style={{ fontSize: 15, color: '#374151', marginBottom: 24 }}>
                    Are you sure you want to send this campaign? Messages will be sent to all selected contacts. This cannot be undone.
                  </div>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowConfirm(false)} style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Cancel</button>
                    <button onClick={handleConfirmed} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Yes, Send Now</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
